import { supabaseClient } from "@/lib/supabase";
import { isUuid } from "@/services/isUuid";
import { queryOptions, useQuery } from "@tanstack/react-query";

export interface UserLabelAccessT {
    label_id: string;
    label_name: string;
}

/**
 * Effective label access (direct grants + study membership).
 * Uses SECURITY DEFINER RPC so admins can inspect another user's grants.
 */
export async function fetchUserLabelAccess(userId: string): Promise<UserLabelAccessT[]> {
    if (!isUuid(userId)) return [];

    const { data, error } = await supabaseClient.rpc("get_user_label_access", {
        p_user_id: userId,
    });

    if (error) throw error;

    return (data ?? [])
        .map((row: { label_id: string; label_name: string | null }) => ({
            label_id: row.label_id,
            label_name: row.label_name ?? "",
        }))
        .filter((r) => r.label_name);
}

export const userLabelAccessQueryKey = (userId: string) => ["user-label-access", userId];

export function userLabelAccessQueryOptions(userId: string | undefined) {
    return queryOptions({
        queryKey: userLabelAccessQueryKey(userId ?? ""),
        queryFn: () => fetchUserLabelAccess(userId ?? ""),
        enabled: isUuid(userId),
    });
}

export function useUserLabelAccessQuery(userId: string | undefined) {
    return useQuery(userLabelAccessQueryOptions(userId));
}

export interface LabelAccessRowT {
    label_id: string;
    user_id: string;
    label_name: string;
}

export async function fetchAllLabelAccess(): Promise<LabelAccessRowT[]> {
    const { data, error } = await supabaseClient
        .from("users_label_access")
        .select("label_id, user_id, diary_labels(label_name)");

    if (error) throw error;

    return (data ?? [])
        .map((row: { label_id: string; user_id: string; diary_labels: { label_name: string } | null }) => ({
            label_id: row.label_id,
            user_id: row.user_id,
            label_name: row.diary_labels?.label_name ?? "",
        }))
        .filter((r) => r.label_name);
}

export const allLabelAccessQueryKey = ["all-label-access"];

export const allLabelAccessQueryOptions = () =>
    queryOptions({
        queryKey: allLabelAccessQueryKey,
        queryFn: () => fetchAllLabelAccess(),
    });

export function useAllLabelAccessQuery() {
    return useQuery(allLabelAccessQueryOptions());
}
